import React, { useMemo } from 'react';
import { TrendingUp, TrendingDown, ChevronUp, ChevronDown } from 'lucide-react';

/**
 * CategorySummaryTable - Displays category performance summary
 * Shows overall accuracy, last/second-last 20Q windows, trends, and performance tiers
 */
const CategorySummaryTable = ({
  data = [],
  isLoading = false
}) => {
  const sortedData = useMemo(() => {
    if (!data || data.length === 0) return [];
    // Already sorted by the backend, but ensure sort is stable
    return [...data].sort((a, b) => b.overall_accuracy - a.overall_accuracy);
  }, [data]);

  const getTrendIcon = (trend) => {
    if (trend === 'improving') {
      return <TrendingUp className="w-5 h-5 text-green-400" />;
    } else if (trend === 'declining') {
      return <TrendingDown className="w-5 h-5 text-red-400" />;
    }
    return <ChevronUp className="w-5 h-5 text-yellow-400" />;
  };

  const getTrendLabel = (trend) => {
    if (trend === 'improving') return 'Improving';
    if (trend === 'declining') return 'Declining';
    return 'Stable';
  };

  const getAccuracyColor = (accuracy) => {
    if (accuracy >= 80) return 'text-green-400';
    if (accuracy >= 70) return 'text-yellow-400';
    if (accuracy >= 60) return 'text-cyan-400';
    return 'text-red-400';
  };

  const getPerformanceTierLabel = (tier) => {
    if (tier === 'top_20') return '🏆 Top 20%';
    if (tier === 'bottom_20') return '⚠️ Bottom 20%';
    return 'Middle';
  };

  const getPerformanceTierColor = (tier) => {
    if (tier === 'top_20') return 'bg-green-400/10 text-green-400 border-green-400/30';
    if (tier === 'bottom_20') return 'bg-red-400/10 text-red-400 border-red-400/30';
    return 'bg-gray-400/10 text-gray-400 border-gray-400/30';
  };

  if (isLoading) {
    return (
      <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 flex flex-col items-center justify-center h-64">
        <div className="text-gray-400 text-center">
          <p className="mb-2">No category data available</p>
          <p className="text-sm text-gray-500">Answer questions in categories to see performance</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-900 border border-gray-700 rounded-xl p-6">
      <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
        <TrendingUp className="w-5 h-5 text-cyan-500" />
        Category Performance Summary
      </h3>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-700">
              <th className="text-left py-3 px-4 font-semibold text-gray-300">
                Category
              </th>
              <th className="text-center py-3 px-4 font-semibold text-gray-300">
                Overall Accuracy
              </th>
              <th className="text-center py-3 px-4 font-semibold text-gray-300">
                Last 20 Q's
              </th>
              <th className="text-center py-3 px-4 font-semibold text-gray-300">
                2nd Last 20 Q's
              </th>
              <th className="text-center py-3 px-4 font-semibold text-gray-300">
                Trend
              </th>
              <th className="text-center py-3 px-4 font-semibold text-gray-300">
                Performance Tier
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((item, index) => (
              <tr key={`${item.category}-${index}`} className="border-b border-gray-800 hover:bg-gray-800/50 transition">
                <td className="py-3 px-4 text-gray-300 font-medium capitalize">
                  {item.category}
                </td>
                <td className="py-3 px-4 text-center">
                  <span className={`${getAccuracyColor(item.overall_accuracy)} font-bold text-lg`}>
                    {item.overall_accuracy}%
                  </span>
                  <div className="text-xs text-gray-500 mt-1">{item.total_attempts} attempts</div>
                </td>
                <td className="py-3 px-4 text-center">
                  <span className={`${getAccuracyColor(item.last_20_accuracy)} font-semibold`}>
                    {item.last_20_accuracy}%
                  </span>
                </td>
                <td className="py-3 px-4 text-center">
                  <span className={`${getAccuracyColor(item.second_last_20_accuracy)} font-semibold`}>
                    {item.second_last_20_accuracy}%
                  </span>
                </td>
                <td className="py-3 px-4 text-center">
                  <div className="flex items-center justify-center gap-2">
                    {getTrendIcon(item.trend)}
                    <span className="text-gray-300 text-xs">{getTrendLabel(item.trend)}</span>
                  </div>
                </td>
                <td className="py-3 px-4 text-center">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getPerformanceTierColor(item.performance_tier)}`}>
                    {getPerformanceTierLabel(item.performance_tier)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend and Notes */}
      <div className="mt-6 pt-4 border-t border-gray-700">
        <p className="text-xs text-gray-400 mb-3">
          <strong>Performance Tiers:</strong> Top 20% indicates strongest categories. Bottom 20% shows opportunities for improvement.
        </p>
        <p className="text-xs text-gray-400">
          <strong>Trend:</strong> Compares last 20 questions vs. second-last 20 questions to identify recent progress.
        </p>
      </div>
    </div>
  );
};

export default CategorySummaryTable;
